import { useEffect, useEffectEvent, useState, type SubmitEvent } from "react";
import { ApiError, type ApiClient } from "../../api/client";
import type { PairingResult } from "../../api/types";
import { t } from "../../i18n";
import { Logo } from "../../ui/Logo";
import { PairingCodeInput } from "./PairingCodeInput";

interface Props {
  api: ApiClient;
  onPaired: (result: PairingResult) => void;
}

/** First start of a new till: the owner's one-time code turns this browser into a paired device. */
export function PairingScreen({ api, onPaired }: Props) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pair = async (entered: string) => {
    setBusy(true);
    setError(null);
    try {
      // a code for the Tafo app (ober, koerier) is refused here and stays valid for the app
      onPaired(
        await api.request<PairingResult>("POST", "/device/pairing", {
          auth: "none",
          body: { code: entered, purpose: "till" },
        }),
      );
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : t.errors.unexpected);
      setBusy(false);
    }
  };

  const submit = (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    void pair(code);
  };

  // Only in the demo (npm run demo): the till pairs itself, no code to type (Furkan, 01/10/2026).
  // A build never asks; without the demo helper the screen simply waits for a code as usual.
  const pairDemo = useEffectEvent((demoCode: string) => {
    setCode(demoCode);
    void pair(demoCode);
  });
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const controller = new AbortController();
    fetch("/__demo/pairing-code", { signal: controller.signal })
      .then(async (response) => (response.ok ? ((await response.json()) as { code?: unknown }) : null))
      .then((answer) => {
        if (typeof answer?.code === "string") pairDemo(answer.code);
      })
      .catch(() => {
        // not the demo: pair with a code from the owner
      });
    return () => {
      controller.abort();
    };
  }, []);

  return (
    <main className="center-screen">
      <form className="card-panel" onSubmit={submit}>
        <Logo />
        <h1>{t.pairing.title}</h1>
        <p className="muted">{t.pairing.intro}</p>
        <label className="field">
          {t.pairing.code}
          <PairingCodeInput value={code} onChange={setCode} autoFocus />
        </label>
        {error !== null && (
          <p className="error-text" role="alert">
            {error}
          </p>
        )}
        <button type="submit" className="btn primary" disabled={busy || code.replace(/[^a-z0-9]/gi, "").length < 8}>
          {busy ? t.pairing.busy : t.pairing.submit}
        </button>
      </form>
    </main>
  );
}
